/**
 * Batch 18-F11.18 — Companion Runtime Capability Detection Helper
 *
 * A pure-TypeScript helper that maps a small set of explicitly-
 * injected runtime *signals* to a recommended
 * `CompanionPlatformAdapterId` from the F11.4 capability model. The
 * helper is intentionally inert:
 *
 *   - It does NOT read `navigator.userAgent` or
 *     `navigator.userAgentData`.
 *   - It does NOT call `window.matchMedia`.
 *   - It does NOT touch `chrome.runtime`, `browser.runtime`, or
 *     `safari.extension`.
 *   - It does NOT send messages to extensions.
 *   - It does NOT call into Supabase, Edge Functions, or any hook.
 *   - It does NOT auto-launch the mobile fallback under any
 *     circumstance — `shouldAutoLaunchFallback` is always `false`.
 *
 * Production launch surfaces must NOT import this helper yet. The
 * helper exists so future UI can *optionally* consult it; the
 * F11.5 → F11.17 wiring continues to render the manual opt-in
 * "Open with mobile fallback" toggle as today.
 *
 * Extension presence, provider-callback availability, and manager-
 * verification availability are modeled as explicit boolean
 * *inputs* on `CompanionRuntimeSignal` — never inferred from
 * browser globals. This keeps the helper deterministic and unit-
 * testable without a DOM.
 */

import type {
  CompanionPlatformAdapterId,
  CompanionEvidenceTrustCeiling,
  CompanionPlatformCapability,
} from './companionPlatformCapabilities';
import { getCompanionPlatformCapability } from './companionPlatformCapabilities';

// ─── Signal & result types ──────────────────────────────────────────

export interface CompanionRuntimeSignal {
  // Inert environment probes (all optional, all defaulted).
  readonly hasDocument?: boolean;
  readonly hasWindow?: boolean;
  readonly hasNavigator?: boolean;
  readonly hasTouchPoints?: boolean;
  readonly maxTouchPoints?: number | null;
  readonly standaloneDisplayMode?: boolean;
  readonly viewportWidth?: number | null;
  readonly viewportHeight?: number | null;

  // Explicit channel inputs — never inferred from browser globals.
  readonly chromiumExtensionAvailable?: boolean;
  readonly firefoxExtensionAvailable?: boolean;
  readonly safariExtensionAvailable?: boolean;
  readonly providerCallbackAvailable?: boolean;
  readonly managerVerificationAvailable?: boolean;
}

export interface CompanionRuntimeCapabilityResult {
  readonly recommendedAdapterId: CompanionPlatformAdapterId;
  readonly reason: string;
  readonly evidenceTrustCeiling: CompanionEvidenceTrustCeiling;
  readonly canUseExtensionObservation: boolean;
  readonly shouldShowMobileFallbackOption: boolean;
  readonly shouldAutoLaunchFallback: false;
  readonly notes: readonly string[];
}

// ─── Pure helpers ───────────────────────────────────────────────────

const MOBILE_VIEWPORT_THRESHOLD_PX = 768;

export function isMobileLikeRuntime(
  signals: CompanionRuntimeSignal = {},
): boolean {
  const touch =
    (typeof signals.maxTouchPoints === 'number' && signals.maxTouchPoints > 0) ||
    signals.hasTouchPoints === true;
  const narrow =
    typeof signals.viewportWidth === 'number' &&
    signals.viewportWidth > 0 &&
    signals.viewportWidth < MOBILE_VIEWPORT_THRESHOLD_PX;
  const standalone = signals.standaloneDisplayMode === true;
  return touch || narrow || standalone;
}

export function isExtensionObservationAvailable(
  signals: CompanionRuntimeSignal = {},
): boolean {
  return (
    signals.chromiumExtensionAvailable === true ||
    signals.firefoxExtensionAvailable === true ||
    signals.safariExtensionAvailable === true
  );
}

// ─── Capability lookup ──────────────────────────────────────────────

function adapterCeiling(
  adapterId: CompanionPlatformAdapterId,
): CompanionEvidenceTrustCeiling {
  const cap: CompanionPlatformCapability | undefined =
    getCompanionPlatformCapability(adapterId);
  return cap?.evidenceTrustCeiling ?? 'self_attested';
}

// ─── Precedence resolver ────────────────────────────────────────────
//
// Order (strongest → weakest):
//   1. provider_lms_callback        (verified)
//   2. manager_verification         (verified)
//   3. chromium_desktop_extension   (likely)
//   4. firefox_desktop_extension    (likely)
//   5. safari_macos_extension OR
//      safari_ios_ipados_extension  (likely)   ← split on mobile-like
//   6. mobile_no_extension_fallback (self_attested)
//
// The mobile fallback toggle is shown on every path. The helper
// NEVER recommends auto-launching the fallback.

export function resolveCompanionRuntimeCapability(
  signals: CompanionRuntimeSignal = {},
): CompanionRuntimeCapabilityResult {
  const notes: string[] = [];

  if (signals.providerCallbackAvailable === true) {
    return {
      recommendedAdapterId: 'provider_lms_callback',
      reason:
        'Provider/LMS completion callback is available — strongest automated proof.',
      evidenceTrustCeiling: adapterCeiling('provider_lms_callback'),
      canUseExtensionObservation: false,
      shouldShowMobileFallbackOption: true,
      shouldAutoLaunchFallback: false,
      notes: [
        'Provider callback outranks extension and manager signals.',
        ...notes,
      ],
    };
  }

  if (signals.managerVerificationAvailable === true) {
    return {
      recommendedAdapterId: 'manager_verification',
      reason:
        'Manager verification is available — human-attested evidence path.',
      evidenceTrustCeiling: adapterCeiling('manager_verification'),
      canUseExtensionObservation: false,
      shouldShowMobileFallbackOption: true,
      shouldAutoLaunchFallback: false,
      notes: [
        'Manager verification outranks extension signals.',
        ...notes,
      ],
    };
  }

  if (signals.chromiumExtensionAvailable === true) {
    return {
      recommendedAdapterId: 'chromium_desktop_extension',
      reason:
        'Chromium desktop extension is reachable — passive observation can satisfy "likely" confidence.',
      evidenceTrustCeiling: adapterCeiling('chromium_desktop_extension'),
      canUseExtensionObservation: true,
      shouldShowMobileFallbackOption: true,
      shouldAutoLaunchFallback: false,
      notes: [
        'Extension observation is passive; never bypasses provider monetization.',
        ...notes,
      ],
    };
  }

  if (signals.firefoxExtensionAvailable === true) {
    return {
      recommendedAdapterId: 'firefox_desktop_extension',
      reason:
        'Firefox desktop extension is reachable — passive observation can satisfy "likely" confidence.',
      evidenceTrustCeiling: adapterCeiling('firefox_desktop_extension'),
      canUseExtensionObservation: true,
      shouldShowMobileFallbackOption: true,
      shouldAutoLaunchFallback: false,
      notes: [
        'Extension observation is passive; never bypasses provider monetization.',
        ...notes,
      ],
    };
  }

  if (signals.safariExtensionAvailable === true) {
    const mobileLike = isMobileLikeRuntime(signals);
    const adapterId: CompanionPlatformAdapterId = mobileLike
      ? 'safari_ios_ipados_extension'
      : 'safari_macos_extension';
    return {
      recommendedAdapterId: adapterId,
      reason: mobileLike
        ? 'Safari Web Extension on iOS/iPadOS is reachable.'
        : 'Safari Web Extension on macOS is reachable.',
      evidenceTrustCeiling: adapterCeiling(adapterId),
      canUseExtensionObservation: true,
      shouldShowMobileFallbackOption: true,
      shouldAutoLaunchFallback: false,
      notes: [
        'Safari extension path is opt-in and observed passively.',
        ...notes,
      ],
    };
  }

  // No extension/provider/manager signals — fall through to the
  // manual mobile fallback. Self-attested ceiling.
  const mobileLike = isMobileLikeRuntime(signals);
  return {
    recommendedAdapterId: 'mobile_no_extension_fallback',
    reason: mobileLike
      ? 'No extension/provider/manager signal; mobile-like runtime detected — show the manual fallback toggle.'
      : 'No extension/provider/manager signal — show the manual fallback toggle.',
    evidenceTrustCeiling: adapterCeiling('mobile_no_extension_fallback'),
    canUseExtensionObservation: false,
    shouldShowMobileFallbackOption: true,
    shouldAutoLaunchFallback: false,
    notes: [
      'Fallback is opt-in. Technician must click "Open with mobile fallback".',
      'Fallback evidence is self-attested and never promotes past that ceiling.',
      ...notes,
    ],
  };
}

// ─── Optional passive runtime collector ─────────────────────────────
//
// The collector only reads inert dimensions / touch points from an
// EXPLICITLY-INJECTED runtime object. It does not read globals on
// its own, and it deliberately ignores `navigator.userAgent` /
// `navigator.userAgentData` / `matchMedia` / extension APIs even
// when they would be available on the injected objects.

export interface PassiveRuntimeInput {
  readonly document?: { readonly defaultView?: unknown } | null;
  readonly window?:
    | { readonly innerWidth?: number; readonly innerHeight?: number }
    | null;
  readonly navigator?: { readonly maxTouchPoints?: number } | null;
}

export function collectPassiveRuntimeSignals(
  runtime: PassiveRuntimeInput = {},
): CompanionRuntimeSignal {
  const w = runtime.window ?? null;
  const n = runtime.navigator ?? null;
  const d = runtime.document ?? null;

  const innerWidth = typeof w?.innerWidth === 'number' ? w.innerWidth : null;
  const innerHeight = typeof w?.innerHeight === 'number' ? w.innerHeight : null;
  const maxTouchPoints =
    typeof n?.maxTouchPoints === 'number' ? n.maxTouchPoints : null;

  return {
    hasDocument: d !== null && d !== undefined,
    hasWindow: w !== null && w !== undefined,
    hasNavigator: n !== null && n !== undefined,
    hasTouchPoints:
      typeof maxTouchPoints === 'number' && maxTouchPoints > 0,
    maxTouchPoints,
    viewportWidth: innerWidth,
    viewportHeight: innerHeight,
  };
}
